import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal } from "reactstrap";
import MetaTags from "react-meta-tags";
import handleExportCSV from "pages/utils/handleExportCSV";
import Datatables from "pages/table/datatable";
import { tableColumns } from "./components/TableColumns";
import StatsCard from "./components/StatsCard";
import FilterBar from "./components/FilterBar";
import { InputField, SelectField, DatePickerField } from "pages/InputFields/InputFields";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";

import { get, post } from "helpers/api_helper";

const LoginAdds = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("Add"); // "Add", "View", or "Edit"
    const [selectedData, setSelectedData] = useState(null);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [formData, setFormData] = useState({ ad_title: "", image: null, position: "", start_date: "", end_date: "", impressions: 0, clicks: 0, status: "Active" });
    const [loginAdsList, setLoginAdsList] = useState([]);

    const fetchLoginAds = useCallback(async () => {
        try {
            const res = await get("/superadmin/login-ads");
            const data = res?.data || res;
            if (Array.isArray(data)) {
                const formatted = data.map((ad, idx) => ({
                    id: ad.id || idx + 1,
                    ad_title: ad.ad_title || "Ad Campaign",
                    image: ad.image || null,
                    position: ad.position || "Popup",
                    start_date: ad.start_date ? new Date(ad.start_date).toLocaleDateString() : "—",
                    end_date: ad.end_date ? new Date(ad.end_date).toLocaleDateString() : "—",
                    impressions: Number(ad.impressions) || 0,
                    clicks: Number(ad.clicks) || 0,
                    status: ad.status || "Active",
                }));
                setLoginAdsList(formatted);
            }
        } catch (e) {
            console.error("Failed to load login ads from database", e);
        }
    }, []);

    useEffect(() => {
        fetchLoginAds();
    }, [fetchLoginAds]);

    const toggleModal = useCallback(() => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setSelectedData(null);
            setFormData({ ad_title: "", image: null, position: "", start_date: "", end_date: "", impressions: 0, clicks: 0, status: "Active" });
        }
    }, [modalOpen]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name, selectedDates) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedDates[0] || null
        }));
    };

    const handleSubmit = async () => {
        const { ad_title, position } = formData;

        if (!ad_title || !position) {
            showErrorAlert("Please fill the required fields (Ad Title & Position).");
            return;
        }

        try {
            const payload = {
                ...formData,
                position: position?.value || position
            };
            await post("/superadmin/login-ads", payload);
            showSuccessAlert("Login Ad Campaign saved to database successfully!");
            setFormData({ ad_title: "", image: null, position: "", start_date: "", end_date: "", impressions: 0, clicks: 0, status: "Active" });
            fetchLoginAds();
            toggleModal();
        } catch (e) {
            showErrorAlert("Failed to save Login Ad to database.");
        }
    };

    const handleAction = (mode, client = null) => {
        setModalMode(mode);
        setSelectedData(client);

        if (mode === "Edit" && client) {
            setFormData({ ad_title: client.ad_title || "", image: client.image || null, position: client.position || "", start_date: client.start_date || "", end_date: client.end_date || "", impressions: client.impressions || 0, clicks: client.clicks || 0, status: client.status || "Active" });
        }
        setModalOpen(true);
    };

    // --- Calculations ---
    const isAnySelected = selectedRowIds.length > 0;

    return (
        <React.Fragment>
            <div className="dash-page ms-4 me-4">
                <MetaTags>
                    <title>Login Adds Management | {process.env.REACT_APP_TITLE}</title>
                </MetaTags>
                <Container fluid>
                    <div className="p-3">
                        <Row className="align-items-center mb-4">
                            <Col md="4">
                                <h4 className="mb-0 fw-bolder">Login Adds Management</h4>
                            </Col>
                            <Col md="8" className="d-flex justify-content-end gap-2">
                                {isAnySelected && (
                                    <>
                                        <button className="btn btn-danger border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                            onClick={() => handleAction("Delete")}>
                                            <i className="bx bx-trash me-1"></i> Delete ({selectedRowIds.length})
                                        </button>
                                        <button className="btn btn-success border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                            onClick={handleExportCSV}>
                                            <i className="bx bx-export me-1"></i> Export All ({selectedRowIds.length})
                                        </button>
                                    </>
                                )}
                                <button className="btn btn-primary border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                    onClick={() => handleAction("Add")}>
                                    <i className="bx bx-plus me-1"></i> New Ad
                                </button>
                            </Col>
                        </Row>

                        <StatsCard loginAdsList={loginAdsList} />

                        <FilterBar />

                        <div className="p-3 bg-white shadow-sm border" style={{ borderRadius: "10px" }}>
                            <Datatables
                                columns={tableColumns(handleAction)}
                                showTableOnly={true}
                                rows={loginAdsList}
                                keyField={"id"}
                                isCheckbox={true}
                                loading={false}
                                setSelectedRowIds={setSelectedRowIds}
                            />
                        </div>
                    </div>


                    <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" className="custom-modal">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold mb-1">{`${modalMode} ${selectedData ? selectedData?.ad_title : 'Add'}`}</h5>
                            <button type="button" onClick={toggleModal} className="close" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {modalMode === "View" && selectedData ? (
                                <>
                                    <div className="text-center p-3">
                                        <div className="avatar-md mx-auto mb-3">
                                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary h2">
                                                {selectedData.ad_title.charAt(0)}
                                            </span>
                                        </div>
                                        <h5 className="mb-1">{selectedData.ad_title}</h5>
                                        <hr />
                                        <Row className="text-start">
                                            <Col xs="6" className="mb-2"><strong>Position:</strong> {selectedData.position}</Col>
                                            <Col xs="6"><strong>Impressions:</strong> {selectedData.impressions}</Col>
                                        </Row>
                                        <Row className="text-start">
                                            <Col xs="6" className="mb-2"><strong>Start Date:</strong> {selectedData.start_date}</Col>
                                            <Col xs="6"><strong>End Date:</strong> {selectedData.end_date}</Col>
                                        </Row>
                                    </div>
                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Close</button>
                                    </div>
                                </>

                            ) : (
                                < div className="p-2">
                                    <Row className="g-3">
                                        <Col md="12">
                                            <InputField label="Add Image" name="image" type="file" value={formData.image} onChange={handleChange} />
                                        </Col>
                                        <Col md="6">
                                            <InputField label="Ad Title" name="ad_title" type="text" value={formData.ad_title} onChange={handleChange} />
                                        </Col>
                                        <Col md="6">
                                            <SelectField label="Position" name="position" isClearable={true} placeholder="Select Position"
                                                value={formData.position} onChange={(option) => setFormData(prev => ({ ...prev, position: option || null }))}
                                                options={[
                                                    { value: "Top Banner", label: "Top Banner" },
                                                    { value: "Sidebar", label: "Sidebar" },
                                                    { value: "Popup", label: "Popup" },
                                                ]}
                                            />
                                        </Col>
                                        <Col md="6">
                                            <DatePickerField label="Start Date" name="start_date" onChange={(dates) => handleDateChange("start_date", dates)}
                                                options={{ dateFormat: "d M, Y", minDate: "today" }} />
                                        </Col>
                                        <Col md="6">
                                            <DatePickerField label="End Date" name="end_date" onChange={(dates) => handleDateChange("end_date", dates)}
                                                options={{ dateFormat: "d M, Y", minDate: "today" }} />
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Cancel</button>
                                        <button className="btn btn-primary px-4" onClick={handleSubmit}
                                            style={{ width: `${modalMode === "Add" ? "110px" : "140px"}` }}>
                                            {modalMode === "Add" ? "Create" : "Save Changes"}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal>
                </Container>
            </div>
        </React.Fragment >
    );
};

export default LoginAdds;
